import isEmpty from "lodash-es/isEmpty";
import { type ActorRefFrom, type ActorRefFromLogic, assign, fromPromise, setup } from "xstate";

import { LOCAL_WORKSPACE_NAME } from "../../../strings";
import { docsApi } from "../../api/docsapi";
import {
	type Invitation,
	type InvitationUpdate,
	type PermissionInfo,
	type SearchUserResult,
	type UpdateUserPermission,
	type UpdateWorkspacePermission,
} from "../../types/permission.types";
import {
	type AuthenticationMachine,
	authenticationActorRef,
	getAccessToken,
} from "../authentication";
import { coerceToError } from "../coerceToError";
import { createRemoteRepo } from "../docsapi/createRemoteRepo";
import { remoteDocumentMetadataExistsPromise } from "../docsapi/remoteDocumentMetadataRepoExists";
import { type gitRepositoryMachine } from "../git/repository/git-repository.machine";

// const debuglog = (_msg, ...args) => {
// 	console.debug(`[sharing machine] ${_msg}`, ...args);
// };
const debuglog = (_msg: string, ..._: unknown[]) => {};

export interface PermissionState {
	read?: PermissionInfo;
	write?: PermissionInfo;
	own?: PermissionInfo;
	invite?: Invitation[];
}

interface SharingModalContext {
	workspace: string;
	remoteWorkspace?: string;
	documentId: string;
	dirty: boolean;
	createdRepo: boolean;
	error: Error | null;
	permissionState: PermissionState | undefined;
	remoteRepoExists: boolean;
	gitRef: ActorRefFrom<typeof gitRepositoryMachine>;
}

interface SharingMachineArgs {
	workspace: string;
	documentId: string;
	authenticationActorRef: ActorRefFromLogic<AuthenticationMachine>;
	gitRef: ActorRefFrom<typeof gitRepositoryMachine>;
}

// In order to avoid doing full consistent reads and writes,
// we do not refresh the data on update success, only on error.
// THus we expect the React component to refresh intelligently when needed.
export const sharingMachine = setup({
	types: {
		context: {} as SharingModalContext,
		events: {} as
			| { type: "sharing.permission.update.user"; payload: UpdateUserPermission }
			| { type: "sharing.permission.update.workspace"; payload: UpdateWorkspacePermission }
			| { type: "sharing.invite.update"; payload: InvitationUpdate }
			| { type: "sharing.attachWorkspace"; workspace: string }
			| { type: "sharing.refresh" },
		input: {} as SharingMachineArgs,
	},

	actors: {
		momentRepoExists: fromPromise(remoteDocumentMetadataExistsPromise),
		createRemoteRepo: fromPromise(createRemoteRepo),
		loadPermissionState: fromPromise(
			async ({
				input,
			}: {
				input: {
					documentId: string;
					workspace: string;
					authenticationActorRef: ActorRefFromLogic<AuthenticationMachine>;
				};
			}) => {
				const { documentId, workspace, authenticationActorRef } = input;

				const token = await getAccessToken(authenticationActorRef);
				if (!token) {
					debuglog("no token");
					return {};
				}

				if (workspace === LOCAL_WORKSPACE_NAME || workspace === "") {
					debuglog(`no workspace ${workspace}`);
					return {};
				}

				// todo - i need to redo this

				const owner = await docsApi.document.permissions.queryUsersWithAccess({
					token,
					workspaceName: workspace,
					documentId,
					permission: "own",
				});

				const readers = await docsApi.document.permissions.queryUsersWithAccess({
					token,
					workspaceName: workspace,
					documentId,
					permission: "read",
				});

				const writers = await docsApi.document.permissions.queryUsersWithAccess({
					token,
					workspaceName: workspace,
					documentId,
					permission: "write",
				});

				const invites = await docsApi.document.permissions.queryInvites({
					token,
					workspaceName: workspace,
					documentId,
				});

				return {
					permissionState: {
						read: readers,
						write: writers,
						own: owner,
						invite: invites,
					},
				};
			}
		),

		updateUserPermissionState: fromPromise(
			async ({
				input,
			}: {
				input: {
					documentId: string;
					workspace: string;
					update: UpdateUserPermission;
					authenticationActorRef: ActorRefFromLogic<AuthenticationMachine>;
				};
			}) => {
				const { documentId, workspace, update, authenticationActorRef } = input;

				const token = await getAccessToken(authenticationActorRef);
				if (!token) {
					throw new Error("Cannot call updateUserPermissionState without token");
				}

				if (workspace === LOCAL_WORKSPACE_NAME || workspace === "") {
					throw new Error("Cannot call updateUserPermissionState without workspace set");
				}

				await docsApi.document.permissions.update({
					token,
					workspaceName: workspace,
					documentId,
					type: update.type,
					relation: update.relation,
					user: update.user,
				});
			}
		),

		updateWorkspacePermissionState: fromPromise(
			async ({
				input,
			}: {
				input: {
					documentId: string;
					workspace: string;
					update: UpdateWorkspacePermission;
					authenticationActorRef: ActorRefFromLogic<AuthenticationMachine>;
				};
			}) => {
				const { documentId, workspace, update, authenticationActorRef } = input;

				const token = await getAccessToken(authenticationActorRef);
				if (!token) {
					throw new Error("Cannot call updateWorkspacePermissionState without token");
				}

				if (workspace === LOCAL_WORKSPACE_NAME || workspace === "") {
					throw new Error(
						"Cannot call updateWorkspacePermissionState without workspace set"
					);
				}

				await docsApi.document.permissions.updateWorkspace({
					token,
					workspaceName: workspace,
					documentId,
					type: update.type,
					relation: update.relation,
				});
			}
		),

		attachWorkspace: fromPromise(
			async ({
				input,
			}: {
				input: {
					workspace: string;
				};
			}) => {
				debuglog("attaching workspace", input.workspace);
				if (input.workspace === LOCAL_WORKSPACE_NAME) {
					throw new Error("Cannot attach to local workspace");
				}
				return {
					workspace: input.workspace,
				};
			}
		),

		updateInviteState: fromPromise(
			async ({
				input,
			}: {
				input: {
					documentId: string;
					workspace: string;
					update: InvitationUpdate;
					authenticationActorRef: ActorRefFromLogic<AuthenticationMachine>;
				};
			}) => {
				const { documentId, workspace, update, authenticationActorRef } = input;

				const token = await getAccessToken(authenticationActorRef);
				if (!token) {
					throw new Error("Cannot call updateInviteState without token");
				}

				if (workspace === LOCAL_WORKSPACE_NAME || workspace === "") {
					throw new Error("Cannot call updateInviteState without workspace set");
				}

				await docsApi.document.permissions.updateInvite({
					token,
					workspaceName: workspace,
					documentId,
					type: update.type,
					relation: update.relation,
					email: update.email,
				});
			}
		),
	},
	guards: {
		isValidWorkspace: ({ context }) =>
			!isEmpty(context.workspace) && context.workspace !== LOCAL_WORKSPACE_NAME,
	},
	actions: {
		// ADD ACTIONS HERE*/
	},
}).createMachine({
	id: "sharing.machine",
	initial: "loading",
	context: ({ input }) => ({
		workspace: input.workspace,
		documentId: input.documentId,
		permissionState: undefined,
		dirty: false,
		error: null,
		remoteRepoExists: false,
		createdRepo: false,
		gitRef: input.gitRef,
	}),
	on: {
		"sharing.attachWorkspace": {
			target: ".attachWorkspace",
			actions: assign({
				dirty: true,
			}),
		},
	},
	states: {
		loading: {
			guard: "isValidWorkspace",
			entry: [() => debuglog("loading permission state")],
			invoke: {
				src: "loadPermissionState",
				input: ({ context }) => ({
					documentId: context.documentId,
					workspace: context.workspace,
					authenticationActorRef,
				}),
				onDone: {
					actions: [
						assign({
							permissionState: ({ event }) => event.output.permissionState,
							dirty: false,
						}),
						({ context }) => {
							debuglog("loaded permission state");
							debuglog(JSON.stringify(context));
						},
					],
					target: "idle",
				},
				onError: {
					target: "error",
					actions: assign({
						error: ({ event }) =>
							"error" in event ? coerceToError(event.error) : null,
					}),
				},
			},
		},
		attachWorkspace: {
			entry: [() => debuglog("attachWorkspace")],
			invoke: {
				src: "attachWorkspace",
				input: ({ event }) => {
					debuglog("attachWorkspace input", event);
					if (event.type !== "sharing.attachWorkspace") {
						throw new Error("Invalid event type");
					}

					return {
						workspace: event.workspace,
						authenticationActorRef,
					};
				},
				onDone: {
					actions: [
						assign({
							workspace: ({ event }) => event.output.workspace,
							dirty: true,
						}),
					],
					target: "idle",
				},
				onError: {
					target: "error",
					actions: [
						({ event }) => console.error("error attaching workspace", { event }),
						assign({
							error: ({ event }) =>
								"error" in event ? coerceToError(event.error) : null,
						}),
					],
				},
			},
		},
		updateUserPermission: {
			entry: [() => debuglog("update permission state")],
			invoke: {
				src: "updateUserPermissionState",
				input: ({ context, event }) => {
					if (event.type !== "sharing.permission.update.user") {
						throw new Error("Invalid event type");
					}
					return {
						documentId: context.documentId,
						workspace: context.workspace,
						update: event.payload,
						authenticationActorRef,
					};
				},
				onDone: {
					actions: [
						() => debuglog("completed update permission state"),
						assign({
							dirty: true,
						}),
					],
					target: "idle",
				},
				onError: {
					target: "error",
					actions: assign({
						error: ({ event }) =>
							"error" in event ? coerceToError(event.error) : null,
					}),
				},
			},
		},
		updateWorkspacePermission: {
			entry: [() => debuglog("update workspace permission")],
			invoke: {
				src: "updateWorkspacePermissionState",
				input: ({ context, event }) => {
					if (event.type !== "sharing.permission.update.workspace") {
						throw new Error("Invalid event type");
					}
					return {
						documentId: context.documentId,
						workspace: context.workspace,
						update: event.payload,
						authenticationActorRef,
					};
				},
				onDone: {
					actions: [
						() => debuglog("updated workspace permission state"),
						assign({
							dirty: true,
						}),
					],
					target: "idle",
				},
				onError: {
					target: "error",
					actions: assign({
						error: ({ event }) =>
							"error" in event ? coerceToError(event.error) : null,
					}),
				},
			},
		},
		updateInvite: {
			entry: [() => debuglog("update invite state")],
			invoke: {
				src: "updateInviteState",

				input: ({ context, event }) => {
					if (event.type !== "sharing.invite.update") {
						throw new Error("Invalid event type");
					}
					return {
						documentId: context.documentId,
						workspace: context.workspace,
						update: event.payload,
						authenticationActorRef,
					};
				},
				onDone: {
					actions: [
						assign({
							dirty: true,
						}),
					],
					target: "idle",
				},
				onError: {
					target: "error",
					actions: assign({
						error: ({ event }) =>
							"error" in event ? coerceToError(event.error) : null,
					}),
				},
			},
		},
		idle: {
			entry: [() => debuglog("in idle state")],
			on: {
				"sharing.refresh": {
					target: "loading",
					actions: [
						assign({
							dirty: true,
						}),
					],
				},
				"sharing.permission.update.user": {
					guard: "isValidWorkspace",
					target: "updateUserPermission",
					actions: [
						(event) => debuglog("updating permission", event),
						assign({
							dirty: true,
						}),
					],
				},
				"sharing.permission.update.workspace": {
					guard: "isValidWorkspace",
					target: "updateWorkspacePermission",
					actions: [
						(event) => debuglog("updating permission", event),
						assign({
							dirty: true,
						}),
					],
				},
				"sharing.invite.update": {
					guard: "isValidWorkspace",
					target: "updateInvite",
					actions: assign({
						dirty: true,
					}),
				},
			},
		},

		error: {
			on: {
				"sharing.refresh": {
					target: "loading",
				},
			},
			entry: [
				(event, context) =>
					console.error("error loading permission state", { event, context }),
			],
		},
	},
});
